import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

function ChevronIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/**
 * "My Account" in the public header: not a single link (there's no single
 * account type to land on) but a small dropdown to the two real portal
 * doors, `/login` (resident/tenant) and `/login/owner`. Same open/outside-
 * click/Escape shape as `SettingsPanel` on the staff side.
 *
 * The two doors are visually distinguished with the public site's own
 * filled-vs-outline button pair (`.button` / `.button--quiet`, both already
 * re-themed teal for `.public` — see styles.css) rather than inventing a
 * third color scheme; pill-shaped here only, via `public__account-pill`,
 * since the square `.button` shape elsewhere on this site is deliberate and
 * this menu is the one place asking for the rounder treatment.
 */
export function AccountMenu() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="public__account-menu" ref={menuRef}>
      <button
        type="button"
        className="public__nav-link public__account-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        My Account
        <ChevronIcon />
      </button>

      {open ? (
        <div className="public__account-panel" role="menu">
          <Link
            to="/login"
            className="button public__account-pill"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            Resident Login
          </Link>
          <Link
            to="/login/owner"
            className="button button--quiet public__account-pill"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            Owner Login
          </Link>
        </div>
      ) : null}
    </div>
  );
}
